import type { VoicedChord } from '@tunasong/music-lib'
import { voiceLeadingChordToEvents } from './chord-to-events.js'
import type { Stab } from './stab-scheduler.js'

/**
 * Voice-Leading Lab audition primitive. Pure function — emits a two-stab
 * "voicing A → gap → voicing B" sequence (or a single voicing on demand for the
 * Explore per-voicing replay buttons). Reuses `voiceLeadingChordToEvents` for
 * the explicit per-voice NoteEvent shape.
 *
 * Timing model (Bowie §19 — single stab-duration constant):
 *  - Each voicing plays as a half-note stab (`'2n'`).
 *  - A fixed 500 ms silence-gap separates A and B in `pair` mode.
 *  - At 100 BPM that's a 1200 ms stab; B therefore starts at 1700 ms and the
 *    sequence ends at 2900 ms.
 *
 * Spec: docs/superpowers/specs/2026-05-27-voice-leading-lab-v1-design.md §5
 */

export const VOICE_LEADING_CHORD_DURATION = '2n' as const
export const VOICE_LEADING_GAP_MS = 500

const QUARTERS_PER_HALF = 2

export type VoiceLeadingSequenceMode = 'pair' | 'voicing-only-a' | 'voicing-only-b'

export interface VoiceLeadingSequencerInput {
  voicingA: VoicedChord
  voicingB: VoicedChord
  bpm: number
  /** 'pair' (default) auditions A → gap → B; single-voicing modes replay one. */
  sequenceMode?: VoiceLeadingSequenceMode
}

export interface VoiceLeadingPhaseBoundaries {
  aStartMs: number
  bStartMs: number
  endMs: number
}

export interface VoiceLeadingStabsResult {
  stabs: Stab[]
  phaseBoundaries: VoiceLeadingPhaseBoundaries
}

export const buildVoiceLeadingStabs = (input: VoiceLeadingSequencerInput): VoiceLeadingStabsResult => {
  const { voicingA, voicingB, bpm, sequenceMode = 'pair' } = input
  const quarterMs = 60000 / bpm
  const stabMs = QUARTERS_PER_HALF * quarterMs

  const aStartMs = 0
  const bStartMs = stabMs + VOICE_LEADING_GAP_MS

  const stabs: Stab[] = []
  if (sequenceMode === 'pair' || sequenceMode === 'voicing-only-a') {
    stabs.push({
      events: voiceLeadingChordToEvents(voicingA, VOICE_LEADING_CHORD_DURATION),
      delayMs: aStartMs,
    })
  }
  if (sequenceMode === 'pair' || sequenceMode === 'voicing-only-b') {
    stabs.push({
      events: voiceLeadingChordToEvents(voicingB, VOICE_LEADING_CHORD_DURATION),
      delayMs: sequenceMode === 'voicing-only-b' ? 0 : bStartMs,
    })
  }

  return {
    stabs,
    phaseBoundaries: {
      aStartMs,
      bStartMs,
      endMs: bStartMs + stabMs,
    },
  }
}
